/**
 * Toast UI primitive — controlled, accessible, auto-dismissing notification.
 *
 * US-008 (T3) / AC-008.6, AC-008.9: the only genuinely new primitive this
 * feature needs. Verified genuinely absent three independent ways before this
 * file was written: zero 'toast' hits across all of frontend/src, no
 * @radix-ui dependency of any kind, no toast library (sonner, react-hot-toast)
 * in package.json. There is nothing to extend, so this is hand-rolled in the
 * same style as every other ui/ primitive.
 *
 * CONTROLLED (checkbox.tsx:10-14 convention): the parent owns the `open` flag
 * and all state; this component owns no internal global state, no provider,
 * no context, no queue, no portal. One toast at a time, raised by the page
 * that owns the flow, is the whole requirement (DD-4) — do not add any of
 * those, a future second consumer is speculative and cheap to refactor for
 * when it actually exists.
 *
 * WCAG posture: role="status" + aria-live="polite" (NOT role="alert" — a
 * success confirmation is not an alert and an assertive announcement would
 * interrupt a screen reader mid-sentence). Focus is never moved to the toast;
 * stealing focus for a transient confirmation is a known accessibility fault.
 * Dismiss control carries a 44x44px touch target (UI_UX_FRAMEWORK 44px rule).
 *
 * Timer cleanup (TO-LL-036 fail-safe form): the auto-dismiss setTimeout lives
 * inside a useEffect whose cleanup always clears it (AddChildDialog.tsx:89-92
 * idiom), so a toast dismissed by hand or unmounted mid-timer can never call
 * setState on a dead component or fire a stale dismiss.
 *
 * BUG-20260803-192629 (RC-003, fixed 2026-08-04): the auto-dismiss timer is
 * governed by `open` and `durationMs` ONLY. It is scheduled once per `open`
 * transition (and re-scheduled if `durationMs` changes) and is NEVER
 * restarted by a parent re-render. onDismiss is read from a ref at fire
 * time (onDismissRef, synced by an effect that runs on every render — see
 * useSessionMessages.ts:209-213 for the same value-in-a-ref idiom), so it no
 * longer needs to sit in the timer effect's dependency array and no longer
 * needs to be referentially stable across renders. Before this fix, onDismiss
 * WAS in that dependency array, so any parent re-rendering faster than
 * durationMs (for example with an inline arrow prop) tore down and
 * rescheduled the timer indefinitely and the toast could never auto-dismiss.
 * Do NOT put onDismiss back in the timer effect's dependency array.
 */

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

export interface ToastProps {
  /** Whether the toast is visible (controlled by the parent). */
  open: boolean;
  /** The message to announce. */
  message: string;
  /** Called when the toast should close — by the auto-dismiss timer or the manual dismiss button. */
  onDismiss: () => void;
  /** Auto-dismiss delay in milliseconds. Defaults to 4000. */
  durationMs?: number;
}

/**
 * Toast — a single, controlled, bottom-anchored notification.
 * Renders nothing (no DOM cost) until `open` is true.
 */
export function Toast({ open, message, onDismiss, durationMs = 4000 }: ToastProps) {
  // BUG-20260803-192629 (RC-003): latest onDismiss, read at fire time, kept
  // OUTSIDE the timer effect's dependency array. Initialised with the prop
  // so the very first timer already reads the correct callback even before
  // the first sync effect below runs.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const timer = setTimeout(() => onDismissRef.current(), durationMs);
    return () => clearTimeout(timer);
  }, [open, durationMs]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4 motion-reduce:transition-none"
      aria-hidden={false}
    >
      <div
        role="status"
        aria-live="polite"
        className="flex max-w-sm items-center gap-3 rounded-2xl bg-otter-earth-800 px-4 py-3 text-sm font-medium text-white shadow-lg"
      >
        <span>{message}</span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
